import { supabase } from '@/integrations/supabase/client';

export interface GameRosterPlayer {
  id: string;
  game_id: string;
  team_id: number;
  player_id: string;
  player_type: 'supabase' | 'internal';
  team_side: 'home' | 'away';
  position?: string;
  is_present: boolean;
  is_eligible: boolean;
  added_by: string;
  player_name?: string;
  username?: string;
}

export const gameRosterService = {
  async resolveGameCodeToId(gameCode: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('venue_games')
      .select('id')
      .eq('game_code', gameCode)
      .single();

    if (error) {
      console.error('Error resolving game code to ID:', error);
      return null;
    }

    return data?.id || null;
  },

  async loadGameRoster(gameId: string): Promise<GameRosterPlayer[]> {
    const { data, error } = await supabase
      .from('game_rosters_live')
      .select('*')
      .eq('game_id', gameId);

    if (error) {
      console.error('Error loading game roster:', error);
      throw error;
    }

    // Get player names separately for better type safety
    const playersWithNames = await Promise.all(
      (data || []).map(async (player) => {
        let player_name = `Player ${player.player_id.substring(0, 8)}`;
        
        if (player.player_type === 'supabase') {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, first_name, last_name')
            .eq('id', player.player_id)
            .single();
          
          if (profile) {
            player_name = profile.username || 
                         `${profile.first_name || ''} ${profile.last_name || ''}`.trim() ||
                         player_name;
          }
        } else {
          const { data: internal } = await supabase
            .from('internal_users')
            .select('username')
            .eq('id', player.player_id)
            .single();
          
          if (internal) {
            player_name = internal.username || player_name;
          }
        }
        
        return {
          ...player,
          player_type: player.player_type as 'supabase' | 'internal',
          team_side: player.team_side as 'home' | 'away',
          player_name
        };
      })
    );
    
    return playersWithNames;
  },

  async addPlayerToGameRoster(
    gameId: string, 
    teamId: number, 
    playerId: string, 
    playerType: 'supabase' | 'internal',
    teamSide: 'home' | 'away',
    position?: string,
    addedByUserId?: string
  ): Promise<GameRosterPlayer> {
    // Validate that addedByUserId is provided
    if (!addedByUserId) {
      throw new Error('addedByUserId is required for roster operations');
    }
    
    // Validate that the adding user exists in either system
    const { data: supabaseUser } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', addedByUserId)
      .single();
      
    const { data: internalUser } = await supabase
      .from('internal_users')
      .select('id')
      .eq('id', addedByUserId)
      .eq('is_active', true)
      .single();
      
    if (!supabaseUser && !internalUser) {
      throw new Error('Invalid user ID provided for adding player');
    }

    const { data, error } = await supabase
      .from('game_rosters_live')
      .insert({
        game_id: gameId,
        team_id: teamId,
        player_id: playerId,
        player_type: playerType,
        team_side: teamSide,
        position,
        is_present: true,
        is_eligible: true,
        game_day_status: 'ready',
        added_by: addedByUserId
      })
      .select()
      .single();

    if (error) throw error;
    return { 
      ...data, 
      player_type: playerType as 'supabase' | 'internal',
      team_side: teamSide as 'home' | 'away'
    };
  },

  async updatePlayerPresence(rosterId: string, isPresent: boolean): Promise<void> {
    const { error } = await supabase
      .from('game_rosters_live')
      .update({ is_present: isPresent })
      .eq('id', rosterId);

    if (error) throw error;
  },

  async removePlayerFromGameRoster(rosterId: string): Promise<void> {
    const { error } = await supabase
      .from('game_rosters_live')
      .delete()
      .eq('id', rosterId);

    if (error) throw error;
  },

  async populateGameRosterFromTeams(gameId: string, homeTeamId?: number, awayTeamId?: number, addedByUserId?: string): Promise<void> {
    if (!addedByUserId) {
      throw new Error('addedByUserId is required for roster operations');
    }

    // Get home team players
    if (homeTeamId) {
      const { data: homePlayers } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', homeTeamId);

      if (homePlayers) {
        for (const player of homePlayers) {
          await this.addPlayerToGameRoster(
            gameId,
            homeTeamId,
            player.user_id,
            (player as any).player_type || player.user_type || 'supabase',
            'home',
            player.position,
            addedByUserId
          ).catch(() => {}); // Ignore duplicates
        }
      }
    }

    // Get away team players
    if (awayTeamId) {
      const { data: awayPlayers } = await supabase
        .from('team_players')
        .select('*')
        .eq('team_id', awayTeamId);

      if (awayPlayers) {
        for (const player of awayPlayers) {
          await this.addPlayerToGameRoster(
            gameId,
            awayTeamId,
            player.user_id,
            (player as any).player_type || player.user_type || 'supabase',
            'away',
            player.position,
            addedByUserId
          ).catch(() => {}); // Ignore duplicates
        }
      }
    }
  }
};